import React, { useEffect, useState } from 'react';

import MainMap from './components/MainMap';
import PublicFileAccess from './components/PublicFileAccess';
import VersionUpdateNotifier from './components/VersionUpdateNotifier';
import { GuestAccessProvider } from './components/GuestAccessProvider';

import 'semantic-ui-css/semantic.min.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet-geosearch/dist/geosearch.css';
import './assets/styles/leaflet-areaselect.css';

const normalizeCodeValue = (value) => {
  const code = (value || '').trim();
  if (code.length < 6 || code.length > 9) return null;
  return code;
};

const normalizeCodeFromPath = (pathname) => {
  const cleanedPath = (pathname || '').split('?')[0];
  if (cleanedPath.startsWith('/f/')) return null;

  const segments = cleanedPath.split('/').filter(Boolean);
  if (!segments.length) return null;

  return normalizeCodeValue(segments[0]);
};

const normalizeCodeFromUrl = () => {
  const params = new URLSearchParams(window.location.search || '');
  const queryCode = normalizeCodeValue(params.get('code'));
  if (queryCode) return queryCode;
  return normalizeCodeFromPath(window.location.pathname);
};

const GuestCodeEntry = ({ initialError = '' }) => {
  const [code, setCode] = useState('');

  const submitCode = (event) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;

    const targetCode = encodeURIComponent(trimmed);
    const isGuestHtmlPath = window.location.pathname.endsWith('/guest.html');
    if (isGuestHtmlPath || import.meta.env.DEV) {
      window.location.href = `/guest.html?code=${targetCode}`;
      return;
    }

    window.location.href = `/${targetCode}`;
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(145deg, #eef4ff 0%, #dde9f8 100%)',
        padding: '24px',
      }}
    >
      <form
        onSubmit={submitCode}
        style={{
          width: '100%',
          maxWidth: '420px',
          background: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 16px 40px rgba(20, 33, 61, 0.15)',
          display: 'grid',
          gap: '12px',
        }}
      >
        <h2 style={{ margin: 0 }}>Open Tour</h2>
        <p style={{ margin: 0, color: '#556070' }}>
          Enter the access code from your cruise director or QR card.
        </p>
        {initialError ? (
          <div style={{ color: '#a02828', fontSize: '14px' }}>{initialError}</div>
        ) : null}
        <input
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="Enter 6-9 character code"
          style={{
            width: '100%',
            border: '1px solid #c8d3e4',
            borderRadius: '10px',
            padding: '12px 14px',
            fontSize: '16px',
          }}
          autoFocus
        />
        <button
          type="submit"
          style={{
            border: 'none',
            borderRadius: '10px',
            background: '#0b6fcf',
            color: '#fff',
            padding: '12px 14px',
            fontSize: '15px',
            cursor: 'pointer',
          }}
        >
          Continue
        </button>
      </form>
    </div>
  );
};

const GuestApp = () => {
  const isPublicFileAccess = window.location.pathname.startsWith('/f/');
  const [pathCode, setPathCode] = useState(() => normalizeCodeFromUrl());
  const [resolveError, setResolveError] = useState('');

  useEffect(() => {
    const updateCode = () => {
      setPathCode(normalizeCodeFromUrl());
      setResolveError('');
    };

    window.addEventListener('popstate', updateCode);
    return () => window.removeEventListener('popstate', updateCode);
  }, []);

  if (isPublicFileAccess) {
    return <PublicFileAccess />;
  }

  if (!pathCode || resolveError) {
    return <GuestCodeEntry initialError={resolveError} />;
  }

  return (
    <GuestAccessProvider guestCode={pathCode} onResolveError={setResolveError}>
      <MainMap enableUrlStateSync={false} />
      <VersionUpdateNotifier />
    </GuestAccessProvider>
  );
};

export default GuestApp;
